import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import { fetchZabbixCoverage } from '@/api/zabbix'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { downloadCsv } from '@/lib/csvExport'
import { formatDateTime } from '@/lib/formatAgent'

function HostTable({ rows, empty }) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">{empty}</p>
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th className="px-4 py-2 font-medium">Host</th>
            <th className="px-4 py-2 font-medium">Visible name</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Host ID</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.zabbix_host_id} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-2 font-medium">{row.host}</td>
              <td className="px-4 py-2 text-muted-foreground">{row.name || '—'}</td>
              <td className="px-4 py-2">{row.status_label}</td>
              <td className="px-4 py-2 text-muted-foreground">{row.zabbix_host_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DeviceTable({ rows, empty }) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">{empty}</p>
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th className="px-4 py-2 font-medium">Device</th>
            <th className="px-4 py-2 font-medium">Customer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.agent_id} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-2">
                <Link
                  to={`/agents/${row.agent_id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {row.hostname || row.display_name || `Device ${row.agent_id}`}
                </Link>
                {row.display_name && row.hostname ? (
                  <p className="text-xs text-muted-foreground">{row.display_name}</p>
                ) : null}
              </td>
              <td className="px-4 py-2 text-muted-foreground">{row.customer_name || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ZabbixCoverageReport({ customerIds = [] }) {
  const { isAdmin } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    fetchZabbixCoverage(customerIds)
      .then((next) => {
        if (!cancelled) setData(next)
      })
      .catch(() => {
        if (!cancelled) {
          setData(null)
          setError('Could not load the Zabbix coverage report.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [customerIds])

  function handleExport() {
    if (!data) return
    const headers = [
      'List',
      'Device',
      'Display name',
      'Customer',
      'Zabbix host',
      'Zabbix name',
      'Status',
      'Host ID',
    ]
    const missing = (data.missing_from_zabbix || []).map((row) => [
      'Missing from Zabbix',
      row.hostname || '',
      row.display_name || '',
      row.customer_name || '',
      '',
      '',
      '',
      '',
    ])
    const extra = (data.missing_from_dash || []).map((row) => [
      'In Zabbix, not in Bekat Dash',
      '',
      '',
      '',
      row.host || '',
      row.name || '',
      row.status_label || '',
      row.zabbix_host_id || '',
    ])
    const stamp = new Date().toISOString().slice(0, 10)
    downloadCsv(`zabbix-coverage-${stamp}.csv`, headers, [...missing, ...extra])
  }

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>
  }

  if (!data?.configured) {
    return (
      <p className="text-sm text-muted-foreground">
        Zabbix is not configured yet.
        {isAdmin ? (
          <>
            {' '}
            Add the URL and API token under{' '}
            <Link to="/admin/settings?section=zabbix" className="text-primary hover:underline">
              Admin → Settings → Zabbix
            </Link>
            , then run Sync now.
          </>
        ) : (
          ' Ask an administrator to add the Zabbix URL and API token.'
        )}
      </p>
    )
  }

  const missing = data.missing_from_zabbix || []
  const extra = data.missing_from_dash || []

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {data.last_synced_at
            ? `Host list synced ${formatDateTime(data.last_synced_at)}`
            : 'No Zabbix host list has been synced yet.'}
          {typeof data.matched === 'number'
            ? ` · ${data.matched} device${data.matched === 1 ? '' : 's'} in this filter match Zabbix.`
            : ''}
          {data.last_status === 'failed' && data.last_error ? ` Last sync failed: ${data.last_error}` : ''}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">Missing from Zabbix</h3>
          <p className="text-sm text-muted-foreground">
            {missing.length} Bekat Dash device{missing.length === 1 ? '' : 's'} with no matching
            Zabbix host.
          </p>
        </div>
        <DeviceTable rows={missing} empty="Every device in this filter has a matching Zabbix host." />
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">In Zabbix, not in Bekat Dash</h3>
          <p className="text-sm text-muted-foreground">
            {extra.length} Zabbix host{extra.length === 1 ? '' : 's'} with no matching Dash device.
            This list is the full Zabbix estate, not limited by the customer filter.
          </p>
        </div>
        <HostTable rows={extra} empty="Every Zabbix host matches a Bekat Dash device." />
      </section>
    </div>
  )
}
